import type { ReceiptSplitSeed } from '../transactions/splitsHelpers';
import type { ReceiptSplitSuggestionUsageType } from './receiptsApi';

type CacheEntry = { receiptId: string; seed: ReceiptSplitSeed };
type RequestEntry = { receiptId: string; request: Promise<ReceiptSplitSeed> };
const cache = new Map<string, CacheEntry>();
const requests = new Map<string, RequestEntry>();

const cloneSeed = (seed: ReceiptSplitSeed): ReceiptSplitSeed => ({
  ...seed,
  split: { ...seed.split, items: seed.split.items.map((item) => ({ ...item, coa: { ...item.coa } })) },
});

export const clearReceiptSplitSuggestionCache = (receiptId?: string): void => {
  for (const [key, entry] of cache) {
    if (!receiptId || entry.receiptId === receiptId) cache.delete(key);
  }
  for (const [key, entry] of requests) {
    if (!receiptId || entry.receiptId === receiptId) requests.delete(key);
  }
};

export const getReceiptSplitSuggestion = async (
  receiptId: string,
  usageType: ReceiptSplitSuggestionUsageType,
  revision: string,
  fetchSuggestion: () => Promise<ReceiptSplitSeed>,
): Promise<ReceiptSplitSeed> => {
  const key = JSON.stringify([receiptId, usageType, revision]);
  const cached = cache.get(key);
  if (cached) return cloneSeed(cached.seed);
  const existing = requests.get(key);
  const entry = existing ?? { receiptId, request: fetchSuggestion() };
  if (!existing) requests.set(key, entry);
  try {
    const seed = await entry.request;
    // Invalidated requests may finish, but cannot repopulate a new receipt/session cache.
    if (requests.get(key) === entry) cache.set(key, { receiptId, seed });
    return cloneSeed(seed);
  } finally {
    if (requests.get(key) === entry) requests.delete(key);
  }
};
